"use client";

import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/lib/i18n/navigation";
import { cn } from "@/lib/utils";
import {
  House,
  MagnifyingGlass,
  VideoCamera,
  User,
} from "@phosphor-icons/react/dist/ssr";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";

type Item = {
  href: string;
  labelKey: "home" | "search" | "teleconsult" | "account";
  matchPrefixes?: string[];
  Icon: PhosphorIcon;
};

// 4 tabs : Accueil · Rechercher · Téléconsulter · Compte
// Les rendez-vous sont visibles directement sur la home (utilisateur connecté).
const ITEMS: Item[] = [
  { href: "/", labelKey: "home", Icon: House },
  {
    href: "/recherche",
    labelKey: "search",
    matchPrefixes: ["/recherche", "/specialites", "/villes", "/medecin"],
    Icon: MagnifyingGlass,
  },
  {
    href: "/live",
    labelKey: "teleconsult",
    matchPrefixes: ["/live", "/mind", "/consult"],
    Icon: VideoCamera,
  },
  {
    href: "/mon-compte",
    labelKey: "account",
    matchPrefixes: ["/mon-compte", "/connexion", "/inscription"],
    Icon: User,
  },
];

function isActive(pathname: string, item: Item): boolean {
  if (item.href === "/") return pathname === "/";
  const candidates = item.matchPrefixes ?? [item.href];
  return candidates.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

const HIDDEN_ON_PREFIXES = [
  "/teleconsult/",
];

function shouldHide(pathname: string): boolean {
  const normalized = pathname.replace(/^\/(fr|ar|en)(?=\/|$)/, "");
  return HIDDEN_ON_PREFIXES.some((p) => normalized.startsWith(p));
}

export function BottomNav() {
  const pathname = usePathname();
  const t = useTranslations("nav.bottomNav");

  if (shouldHide(pathname)) return null;

  return (
    <nav
      data-bottom-nav
      aria-label={t("ariaLabel")}
      className={cn(
        "fixed inset-x-0 bottom-0 z-50",
        "border-t border-[var(--sahha-border-subtle)] bg-white",
        "pt-2 pb-[max(env(safe-area-inset-bottom),0.625rem)] lg:hidden",
      )}
      style={{ boxShadow: "0 -4px 16px -4px rgba(15,30,58,0.06)" }}
    >
      <ul className="grid grid-cols-4">
        {ITEMS.map((item) => {
          const active = isActive(pathname, item);
          const Icon = item.Icon;
          return (
            <li key={item.href}>
              <Link
                href={item.href as never}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 px-1 py-1 transition-colors",
                  active
                    ? "text-[var(--sahha-primary-700)]"
                    : "text-[var(--sahha-text-500)]",
                )}
              >
                <Icon size={22} weight={active ? "fill" : "regular"} />
                <span
                  className={cn(
                    "block w-full whitespace-nowrap text-center text-[11px] leading-tight",
                    active ? "font-bold" : "font-semibold",
                  )}
                >
                  {t(item.labelKey)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
